/**
 * Font Loading Configuration
 * 
 * Configure font loading behavior for the application
 */

export const fontConfig = {
  /**
   * Font loading timeout in milliseconds
   * If fonts don't load within this time, app will proceed without custom fonts
   * 
   * Recommended values:
   * - 3000 (3 seconds) - Fast networks, good user experience
   * - 5000 (5 seconds) - Default, balanced approach
   * - 10000 (10 seconds) - Slow networks, ensure fonts load
   * - 0 - Disable timeout (wait forever - not recommended)
   */
  loadingTimeout: 5000,

  /**
   * Whether to show loading screen while fonts load
   * If false, app will render immediately with system fonts
   */
  showLoadingScreen: true,

  /**
   * Whether to use fallback fonts if loading fails
   * If true, app continues with system fonts
   * If false, app keeps trying to load fonts
   */
  useFallbackFonts: true,

  /**
   * Whether to log font loading status to console
   * Useful for debugging font loading issues
   */
  enableLogging: true,

  /**
   * Retry configuration
   */
  retry: {
    /**
     * Whether to retry loading fonts if first attempt fails
     */
    enabled: false,

    /**
     * Number of retry attempts
     */
    maxAttempts: 2,

    /**
     * Delay between retry attempts in milliseconds
     */
    retryDelay: 1000,
  },
};

/**
 * Get font loading timeout from config
 * Can be overridden by environment variable
 */
export const getFontLoadingTimeout = () => {
  // Check for environment variable override
  if (process.env.FONT_LOADING_TIMEOUT) {
    return parseInt(process.env.FONT_LOADING_TIMEOUT, 10);
  }
  
  return fontConfig.loadingTimeout;
};

/**
 * Check if font loading timeout is enabled
 */
export const isFontTimeoutEnabled = () => {
  return getFontLoadingTimeout() > 0;
};

/**
 * Log font loading status if logging is enabled
 */
export const logFontStatus = (message, type = 'info') => {
  if (!fontConfig.enableLogging) return;
  
  const prefix = '🔤 [Font Loading]';
  
  switch (type) {
    case 'error':
      console.error(`${prefix} ❌`, message);
      break;
    case 'warn':
      console.warn(`${prefix} ⚠️`, message);
      break;
    case 'success':
      console.log(`${prefix} ✅`, message);
      break;
    default:
      console.log(`${prefix}`, message);
  }
};

